///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { TileSeries } from "@/earthbrowser/datasets/TileSeries"
import type { WebMercatorTileHitCounter } from "./WebMercatorTileLevel"
import { WebMercatorOverlayTile } from "./WebMercatorOverlayTile"
import { type TileInterface } from "./EarthTile"

export class WebMercatorOverlayTileSet {
	private context: EarthBrowserContext
	private countThreshold: number
	private loadingQueueLength: number
	private purgableTimeSecs: number
	public tileSeries: TileSeries
	private tiles = new Map<string, WebMercatorOverlayTile>()
	private bins: WebMercatorTileHitCounter[] = []

	constructor(
		context: EarthBrowserContext,
		countThreshold: number,
		loadingQueueLength: number,
		purgableTimeSecs: number
	) {
		this.context = context
		this.tileSeries = new TileSeries(context)
		this.countThreshold = countThreshold
		this.loadingQueueLength = loadingQueueLength
		this.purgableTimeSecs = purgableTimeSecs
	}

	public updatePriorities(bins: WebMercatorTileHitCounter[]): void {
		this.bins = bins
		const now = performance.now() * 0.001

		const tiles = Array.from(this.tiles.values())
		tiles.forEach(tile => { tile.hitCount = 0 })

		bins.forEach(bin => {
			const tile = this.tiles.get(bin.id.key)
			if (tile) {
				tile.hitCount = bin.count
				if (bin.count >= this.countThreshold) {
					tile.lastAccess = now
				}
			} else if (bin.count >= this.countThreshold) {
				const parentMaterial = this.tiles.get(bin.id.parent.key)?.material
				if (!parentMaterial || parentMaterial.readyToBeAParent) {
					const newTile = new WebMercatorOverlayTile(this.context, bin.id, this.tileSeries, parentMaterial)
					newTile.hitCount = bin.count
					newTile.lastAccess = now
					this.tiles.set(bin.id.key, newTile)
				}
			}
		})
	}

	private tileExpired(tile: WebMercatorOverlayTile, now: number): boolean {
		const expired =
			tile.lastAccess < now - this.purgableTimeSecs &&
			tile.hitCount < this.countThreshold
		return expired
	}

	public pruneDormantTiles(): void {
		const now = performance.now() * 0.001
		const tiles = Array.from(this.tiles.values())

		// Never prune a tile whose texture a surviving child still samples
		// for its parent-fade blend - destroying it would leave the child
		// referencing a deleted GL texture
		const referencedParents = new Set<string>()
		tiles.forEach(tile => {
			if (!this.tileExpired(tile, now)) {
				referencedParents.add(tile.tileId.parent.key)
			}
		})

		const purgableKeys = tiles
			.filter(tile => this.tileExpired(tile, now) && !referencedParents.has(tile.tileId.key))
			.map(tile => tile.tileId.key)
		purgableKeys.forEach(key => {
			const tile = this.tiles.get(key)
			if (tile) {
				tile.destroy()
				this.tiles.delete(key)
			}
		})
	}

	public queueLoading(): void {
		const now = performance.now() * 0.001

		const loading = this.loading()
		if (loading.length >= this.loadingQueueLength) {
			return
		}

		let loadingCount = loading.length
		const pendingQueue = this.bins
			.filter(bin => bin.count >= this.countThreshold)
			.sort((a, b) => {
				const distDiff = a.centerDistance - b.centerDistance
				if (Math.abs(distDiff) > 0.01) return distDiff
				return b.count - a.count
			})

		for (const bin of pendingQueue) {
			const tile = this.tiles.get(bin.id.key)
			if (!tile) {
				const parentTile = this.tiles.get(bin.id.parent.key)
				if (!parentTile || parentTile.readyToBeParent) {
					const newTile = new WebMercatorOverlayTile(this.context, bin.id, this.tileSeries, parentTile?.material)
					newTile.hitCount = bin.count
					newTile.lastAccess = now
					this.tiles.set(bin.id.key, newTile)
					++loadingCount
				}
			}
			if (loadingCount >= this.loadingQueueLength) {
				return
			}
		}
	}

	public allTiles(): TileInterface[] {
		return Array.from(this.tiles.values())
	}

	public loading(): WebMercatorOverlayTile[] {
		const tiles = Array.from(this.tiles.values())
		return tiles.filter(tile => !tile.material.readyToBeAParent)
	}

	public renderable(): WebMercatorOverlayTile[] {
		const tiles = Array.from(this.tiles.values())
		return tiles.filter(tile => tile.material.textureLoaded)
	}

	public destroyAll(): void {
		const tiles = Array.from(this.tiles.values())
		tiles.forEach(tile => tile.destroy())
		this.tiles.clear()
		this.bins = []
	}

	/**
	 * Update all tile materials from the current TileSeries state.
	 * Call this each frame after updating the TileSeries.
	 */
	public updateMaterialsFromSeries(): void {
		const tiles = Array.from(this.tiles.values())
		tiles.forEach(tile => tile.material.updateFromSeries())
	}
}


